// @ts-strict-ignore
import type { PopulatedThreshold, RuleObject } from '~/dashboard/views/workflows/store/engine/types';
import { getNumericPathIndices, getParentRulePathIndicies } from '~/dashboard/views/workflows/service/actions';
import { createStringOfPathArray } from '~/dashboard/views/workflows/store/engine/utils';
import type { IsolatedEditingBlockOrigin } from '../types';
import {
  CaretPositionSuffixTypes,
  MLAAttributeItemOrigin,
  RuleEditorDropdownMode,
  ThresholdRightAttributeOrigin,
} from '../types';

// Provides a default map if no threshold is provided
// or will provide a map based on threshold and editing an operator.
// The value of each key is the target index in a rule for each editing mode
export const getRulePathIndexMap = (
  populatedThreshold?: PopulatedThreshold,
  canConfigureOperator?: boolean,
) => {
  let map: Record<string, number> = {
    [RuleEditorDropdownMode.THRESHOLD_LEFT_ACTION]: 0,
  };

  if (populatedThreshold?.left.attribute) {
    map = {
      [RuleEditorDropdownMode.THRESHOLD_LEFT_ACTION]: 0,
      [RuleEditorDropdownMode.THRESHOLD_LEFT_ATTRIBUTE]: 1,
    };
  }

  if (canConfigureOperator) {
    map = {
      ...map,
      [RuleEditorDropdownMode.THRESHOLD_OPERATOR]: Object.values(map).at(-1) + 1,
    };
  }

  if (populatedThreshold?.right.action) {
    map = {
      ...map,
      [RuleEditorDropdownMode.THRESHOLD_RIGHT_ACTION]: Object.values(map).at(-1) + 1,
      [RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE]: Object.values(map).at(-1) + 2,
    };
  }

  return map;
};


// Transforms the caret position string to a rule path
// `or-4!groupEnd` -> `or[4]`
export const getRulePathInDotNotationFormat = ({
  itemFlatCaret,
  isThresholdCaretPosition = false,
  isRuleOperator = false,
  sourceAction,
}) => {
  if ('id' in (sourceAction.rules as RuleObject)) return '';

  const pathIndices = getNumericPathIndices(itemFlatCaret, 'rules');
  if (!pathIndices) return undefined;

  const parentPathIndicies = getParentRulePathIndicies(pathIndices);
  const rulePath = createStringOfPathArray(pathIndices);

  if (isRuleOperator) {
    // and/or operator change requires a parent path which the path ends with and / or
    // only needs to know where the and/or operator lives at, and do not need to know the very very specific location of which and/or operator within a group.
    // (Because say if user changed the 2nd or in this rule: th1 or th2 or th3 << the new behavior will just flip all ors in that scope level)
    const parentRulePath = createStringOfPathArray(parentPathIndicies);
    return parentRulePath;
  }

  if (isThresholdCaretPosition) {
    // remove the last item if suffix type is any of those
    // because they have an extra -0,1,2,3,4... for keyboard caret dropdown navigation mapping purposes.
    return createStringOfPathArray(parentPathIndicies);
  }

  return rulePath;
};


export function getRuleItemPathFromFlattenedPath(path: string) {
  return path.split('!')[0]; // ! is the delimiter
}


export function getFlattenedPathAsArray(path: string) {
  return path.split('-');
}


export function getSingleThresholdRulePath(lineIndices) {
  let targetItem = lineIndices[0];
  if (lineIndices[1] && (
    !lineIndices[1].endsWith(CaretPositionSuffixTypes.RULE_THEN_ACTION_TYPE)
    && !lineIndices[1].endsWith(CaretPositionSuffixTypes.RULE_THEN_ACTION_VALUE)
  )) {
    [, targetItem] = lineIndices;
  }
  const targetItemFlattenedPath = getRuleItemPathFromFlattenedPath(targetItem);
  const flattenedPathInArray = getFlattenedPathAsArray(targetItemFlattenedPath);
  const flattenedPathInArrayWithoutLastItem = flattenedPathInArray
    .slice(0, flattenedPathInArray.length - 1); // last item in this path would be extra -0, -1, ... for dropdown mapping. in here, we only need the threshold location as in path
  const rulePath = createStringOfPathArray(flattenedPathInArrayWithoutLastItem);

  return rulePath;
}

export const isMLAAttributeItemOrigin = (origin: IsolatedEditingBlockOrigin) => (
  Object.values(MLAAttributeItemOrigin).includes(origin)
);

export const isThresholdRightAttributeOrigin = (origin: IsolatedEditingBlockOrigin) => (
  Object.values(ThresholdRightAttributeOrigin).includes(origin)
);
